import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  Abi,
  AbiFunction,
  BaseError,
  Client,
  createPublicClient,
  createWalletClient,
  fallback,
  custom,
  encodeFunctionData,
  formatEther,
  Hex,
  http,
  InsufficientFundsError,
  parseEther,
  PublicClient,
  TransactionReceipt,
  Transport,
  WalletClient,
  WriteContractParameters,
} from 'viem';
import { PrivateKeyAccount, privateKeyToAccount } from 'viem/accounts';
import { environment } from '../../environments/environment';
import { createAppChain, EXPLORER_LINK } from '../data/chain';
import { FrensTicketPayload, Request, RequestType } from '../types/server';
import type { SignedCommit } from '../generated/sim/commit-manager/SignedCommitLib';
import { bigintReplacer, formatEth, parseTypehashToEip712Types } from './utils.service';
import { Address } from '../data/address-config';
import { isDeployedSite } from '../data/deploy-host';
import {
  SIGNED_COMMIT_TYPEHASH,
  DUAL_SIGNED_REVEAL_TYPEHASH,
  DUAL_SIGNED_SLOT_REVEAL_TYPEHASH,
  CHOMP_ENGINE_DOMAIN,
  SIGNED_COMMIT_MANAGER_DOMAIN,
} from '../generated/eip712Meta';

// Mirrors SignedCommitLib.DualSignedReveal — revealerSalt is uint104,
// revealerExtraData is uint16; viem signs them as bigint/number.
export interface DualSignedReveal {
  battleKey: string;
  turnId: bigint;
  committerMoveHash: string;
  revealerMoveIndex: number;
  revealerSalt: bigint;
  revealerExtraData: number;
}

// Mirrors SignedCommitLib.DualSignedSlotReveal (2-slot flow) — committerMovesHash is keccak of the
// whole committer side word; revealerSidePacked is the full 128-bit revealer side word (uint256).
export interface DualSignedSlotReveal {
  battleKey: string;
  turnId: bigint;
  committerMovesHash: string;
  revealerSidePacked: bigint;
}

// Derive EIP-712 types from the canonical typehash strings (generated from the contracts).
const SignedCommitTypes = parseTypehashToEip712Types(SIGNED_COMMIT_TYPEHASH).types;
const DualSignedRevealTypes = parseTypehashToEip712Types(DUAL_SIGNED_REVEAL_TYPEHASH).types;
const DualSignedSlotRevealTypes = parseTypehashToEip712Types(DUAL_SIGNED_SLOT_REVEAL_TYPEHASH).types;
import { KeyStorage } from './key-storage';
import { OnchainStateService } from './onchain-state.service';
import { ServerService } from './server.service';
import { Web3ErrorService } from './web3-error.service';

import type {
  EIP1193Provider,
  EIP6963AnnounceProviderEvent,
  EIP6963ProviderDetail,
  EIP6963ProviderInfo,
} from '../types/eip6963';

export type { EIP1193Provider, EIP6963ProviderDetail, EIP6963ProviderInfo };

// What wallet_switchEthereumChain / wallet_addEthereumChain need to name a chain.
export interface ProviderChain {
  chainId: number;
  chainName: string;
  rpcUrl: string;
  explorerUrl?: string;
}

function realtimeSendRawTransaction(parameters: {
  serializedTransaction: Hex;
}) {
  return async (client: Client<Transport>): Promise<TransactionReceipt> => {
    // Use transport.request directly to bypass viem's RPC method validation
    const result = await client.transport.request({
      method: 'realtime_sendRawTransaction',
      params: [parameters.serializedTransaction],
    });
    return result as TransactionReceipt;
  };
}

type ExtendedWalletClient = WalletClient & {
  realtimeSendRawTransaction(parameters: {
    serializedTransaction: Hex;
  }): Promise<TransactionReceipt>;
};

export interface TxData {
  txHash: Hex;
  timestamp: number;
  functionName: string;
  txData: any;
  status: boolean;
  turnId?: number;
}

@Injectable({
  providedIn: 'root',
})
export class Wallet {
  private keyStorage = inject(KeyStorage);
  private server = inject(ServerService);

  private TIME_DENOM = 100000;
  private rpc: string = environment.rpcEndpoint;
  public USERNAME_KEY = 'username';
  private static readonly EXTERNAL_PREF_KEY = 'munch:useExternalWallet';
  private static readonly EXTERNAL_PROVIDER_RDNS_KEY = 'munch:externalProviderRdns';
  private static readonly TRANSFER_GAS = 60000n;

  public account: PrivateKeyAccount | undefined;
  public walletClient: ExtendedWalletClient | undefined;
  public externalWalletClient: WalletClient | undefined;
  public publicClient: PublicClient;
  public walletConnectedEvent: BehaviorSubject<boolean>;
  public nonce: number = 0;

  // Resolves once this account's nonce has been fetched. Burner sends await it so an
  // auto-fired tx (e.g. registry migrate, which reacts to onchainState.isLoaded()) can't
  // sign with the default nonce 0 before sign-in's parallel nonce fetch lands.
  private nonceReady: Promise<void> = Promise.resolve();
  private resolveNonceReady: (() => void) | null = null;

  // Burner sends sign against the locally-tracked nonce, so they must not interleave —
  // independent services (registry migration, matchmaker approval, team setup) can each
  // fire one on the same load tick. External-wallet sends are the provider's to sequence.
  private burnerSends: Promise<unknown> = Promise.resolve();

  // Cached at sign-in (and refreshed on tx failure) so explicit-gas burner txs sign locally
  // with zero preflight RPCs. null = unknown; those txs use viem's fee-filling prepare path.
  private cachedMaxFeePerGas: bigint | null = null;

  private autoFaucetFired = false;

  private onchainState = inject(OnchainStateService);
  private web3Error = inject(Web3ErrorService);

  public txHashEvent: Subject<TxData> = new Subject<TxData>();
  public usernameSet: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );

  public walletAddress = signal<Lowercase<Hex> | undefined>(undefined);
  public externalWalletAddress = signal<Lowercase<Hex> | undefined>(undefined);
  public username = signal<string>('');
  public txList = signal<TxData[]>([]);
  public gameAccountBalance = signal<bigint>(0n);
  public externalWalletBalance = signal<bigint>(0n);
  public showFundingModal = signal<boolean>(false);

  // Persisted via EXTERNAL_PREF_KEY + EXTERNAL_PROVIDER_RDNS_KEY so we can
  // silently reconnect next visit.
  public useExternalWalletForTransactions = signal<boolean>(false);

  // Cleared on ws reconnect — server lost session, must re-auth.
  private lastAuthedAddress: Lowercase<Hex> | undefined = undefined;

  // Replayed on ws-reconnect re-auth so we don't re-prompt within the same
  // TIME_DENOM bucket.
  private cachedAuth: {
    address: Lowercase<Hex>;
    timestamp: number;
    signature: Hex;
  } | null = null;

  // In-flight sendAddressAuth, so the ws-reconnect handler joins the existing
  // promise instead of queuing a second wallet prompt.
  private pendingAuth: { address: Lowercase<Hex>; promise: Promise<void> } | null = null;

  // EIP-6963 RDNS of the active external provider; mirrored into localStorage
  // for auto-reconnect on next page load.
  private connectedProviderRdns = signal<string | undefined>(undefined);
  private savedProviderRdns: string | null = null;
  private autoReconnectAttempted = false;

  public connectedProviderInfo = computed<EIP6963ProviderInfo | undefined>(() => {
    const rdns = this.connectedProviderRdns();
    if (!rdns) return undefined;
    return this.availableWallets().find(p => p.info.rdns === rdns)?.info;
  });

  public activeAddress = computed<Lowercase<Hex> | undefined>(() => {
    if (this.useExternalWalletForTransactions()) {
      return this.externalWalletAddress();
    }
    return this.walletAddress();
  });

  public activeBalance = computed<bigint>(() => {
    if (this.useExternalWalletForTransactions()) {
      return this.externalWalletBalance();
    }
    return this.gameAccountBalance();
  });

  private eip6963Providers = new Map<string, EIP6963ProviderDetail>();
  public availableWallets = signal<EIP6963ProviderDetail[]>([]);

  constructor() {
    this.publicClient = createPublicClient({
      chain: createAppChain(this.rpc),
      transport: this.directFirstTransport(true),
    });

    this.walletConnectedEvent = new BehaviorSubject<boolean>(false);

    // Re-authenticate on WebSocket reconnect — server lost session.
    this.server.reconnected.subscribe(() => {
      this.lastAuthedAddress = undefined;
      this.sendAddressAuth();
    });

    // Keep belch's stored address in sync with the active wallet. First-time
    // auth on this ws sends ADDRESS_AUTH directly; a switch to a different
    // address closes the socket so the reconnect path re-auths on a fresh ws,
    // letting the server's disconnect handler clean up identity-keyed state.
    effect(() => {
      const current = this.activeAddress();
      if (this.lastAuthedAddress && current && current !== this.lastAuthedAddress) {
        this.server.forceReconnect();
        return;
      }
      void this.sendAddressAuth();
    });

    // Read saved external-wallet choice once so per-announcement matching is
    // a single in-memory string compare, not two localStorage reads.
    this.savedProviderRdns = this.readSavedProviderRdns();

    this.initializeEIP6963();

    this.loadTxListFromSession();
    effect(() => {
      const list = this.txList();
      try {
        sessionStorage.setItem(Wallet.TX_LIST_STORAGE_KEY, JSON.stringify(list, bigintReplacer));
      } catch {}
    });
  }

  private static readonly TX_LIST_STORAGE_KEY = 'munch:txList';
  private static readonly TX_LIST_CAP = 100;

  private loadTxListFromSession(): void {
    try {
      const raw = sessionStorage.getItem(Wallet.TX_LIST_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) this.txList.set(parsed);
    } catch {}
  }

  private decodeTxArgs(
    abi: Abi | undefined,
    functionName: string | undefined,
    args: unknown,
  ): unknown {
    if (!Array.isArray(args) || args.length === 0) return [];
    if (!abi || !functionName) return [...args];
    const fn = abi.find(
      (item): item is AbiFunction => item.type === 'function' && item.name === functionName,
    );
    if (!fn || !fn.inputs || fn.inputs.length !== args.length) return [...args];
    const named: Record<string, unknown> = {};
    fn.inputs.forEach((input, i) => {
      named[input.name || `arg${i}`] = args[i];
    });
    return named;
  }

  private recordContractTx<TAbi extends Abi>(
    txHash: Hex,
    tx: WriteContractParameters<TAbi>,
    status: boolean,
    turnId?: number,
  ): void {
    const txData: TxData = {
      txHash,
      timestamp: Date.now(),
      functionName: tx.functionName,
      txData: this.decodeTxArgs(tx.abi, tx.functionName, tx.args),
      status,
      turnId,
    };
    this.txHashEvent.next(txData);
    // Capped: only the debug modal reads history, and every update re-serializes to sessionStorage.
    this.txList.update((txs) => [txData, ...txs].slice(0, Wallet.TX_LIST_CAP));
  }

  /** Faucet drips only surface a pending hash — no decoded args or txList entry. */
  private emitFaucetTx(txHash: Hex, functionName: string): void {
    this.txHashEvent.next({
      txHash,
      timestamp: Date.now(),
      functionName,
      txData: {},
      status: false,
    });
  }

  private initializeEIP6963(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener(
      'eip6963:announceProvider',
      this.handleProviderAnnouncement.bind(this)
    );

    window.dispatchEvent(new Event('eip6963:requestProvider'));
  }

  private handleProviderAnnouncement(event: Event): void {
    const announceEvent = event as EIP6963AnnounceProviderEvent;
    const { info, provider } = announceEvent.detail;

    if (!this.eip6963Providers.has(info.uuid)) {
      const detail: EIP6963ProviderDetail = { info, provider };
      this.eip6963Providers.set(info.uuid, detail);
      this.updateAvailableWallets();
      void this.tryAutoReconnectExternal(detail);
    }
  }

  private updateAvailableWallets(): void {
    const walletsList = Array.from(this.eip6963Providers.values());
    this.availableWallets.set(walletsList);
  }

  getAvailableWallets(): EIP6963ProviderDetail[] {
    return Array.from(this.eip6963Providers.values());
  }

  async getOrSetPw() {
    const pw = await this.keyStorage.getOrSetPw();
    return pw;
  }

  async signInOrCreateWallet(password: string) {
    const pk = await this.keyStorage.getOrSetKey(password);
    if (pk != null) {
      await this.signInWithPrivateKey(pk);
    }
  }

  // Sign in with a raw private key (for importing wallets without touching localStorage).
  async signInWithPrivateKey(pk: Hex) {
    // Arm the nonce barrier for this account before account/client are set, so any send
    // racing in during sign-in waits rather than signing with a stale/default nonce.
    this.nonceReady = new Promise<void>((resolve) => { this.resolveNonceReady = resolve; });

    const account = privateKeyToAccount(pk);
    this.account = account;
    this.walletAddress.set(
      this.account.address.toLowerCase() as Lowercase<Hex>
    );

    this.walletClient = createWalletClient({
      chain: this.publicClient.chain,
      transport: this.directFirstTransport(),
    }).extend((client) => ({
      realtimeSendRawTransaction: (parameters: {
        serializedTransaction: Hex;
      }) => realtimeSendRawTransaction(parameters)(client),
    })) as ExtendedWalletClient;

    const username = localStorage.getItem(this.USERNAME_KEY);
    if (username) {
      this.username.set(username);
      await this.sendAddressAuth();
    }

    // Fetch nonce, fee baseline, and onchain state in parallel.
    // Assign + unblock burner sends the instant the nonce lands — independent of the slower
    // state/fee reads. `finally` releases the barrier even on RPC failure (send then fails and
    // self-heals via resyncNonceAndFees) so a transient nonce read can't hang every send.
    const noncePromise = this.publicClient.getTransactionCount({ address: this.account.address })
      .then(nonce => { this.nonce = nonce; })
      .finally(() => this.resolveNonceReady?.());

    // Cache fees once so per-tx signing needs no preflight reads (2x base = refund-safe cushion).
    const feePromise = this.publicClient
      .getBlock({ blockTag: 'latest' })
      .then((block) => {
        this.cachedMaxFeePerGas = block.baseFeePerGas != null ? block.baseFeePerGas * 2n : null;
      })
      .catch(() => { /* no cache — txs fall back to viem's fee-filling prepare path */ });

    const statePromise = this.onchainState.loadStateForAddress(this.account.address);

    await Promise.all([noncePromise, statePromise, feePromise]);

    const ethBalance = this.onchainState.ethBalance();
    this.gameAccountBalance.set(ethBalance);

    this.walletConnectedEvent.next(true);

    this.maybeAutoFaucet();
  }

  private async writeViaExternalWallet<TAbi extends Abi>(tx: WriteContractParameters<TAbi>): Promise<Hex> {
    if (!this.externalWalletClient || !this.externalWalletAddress()) {
      throw new Error('External wallet not connected. Please reconnect your wallet to continue.');
    }
    return this.externalWalletClient.writeContract({
      ...tx,
      account: this.externalWalletAddress()!,
      chain: this.publicClient.chain,
    } as any);
  }

  async sendTxAndEmitHashButDontWait<TAbi extends Abi>(
    tx: WriteContractParameters<TAbi>,
    turnId?: number
  ) {
    if (this.useExternalWalletForTransactions()) {
      try {
        const txHash = await this.writeViaExternalWallet(tx);
        this.recordContractTx(txHash, tx, false, turnId);
        return txHash;
      } catch (error: any) {
        this.handleTxError(error);
        throw error;
      }
    }

    return this.sendViaBurner(tx, turnId);
  }

  // Burner-unconditional write — ignores the external-wallet toggle, for txs that must never
  // raise a wallet prompt (e.g. wager auto-claim: claimShares pays claimants regardless of sender).
  async sendViaBurner<TAbi extends Abi>(
    txInput: Omit<WriteContractParameters<TAbi>, 'account' | 'chain'>,
    turnId?: number
  ) {
    const tx = txInput as WriteContractParameters<TAbi>;
    if (!this.walletClient) {
      throw new Error('Wallet not connected');
    }

    // Don't sign until this account's nonce is loaded (no-op once sign-in has completed).
    await this.nonceReady;

    return this.queueBurnerSend(async () => {
      try {
        let txHash: Hex;
        if (tx.gas && this.cachedMaxFeePerGas != null) {
          // Explicit gas + cached fees: sign locally and submit in a single RPC.
          const signed = await this.signBurnerTx(tx, tx.gas);
          txHash = await this.walletClient!.sendRawTransaction({ serializedTransaction: signed });
        } else {
          // No cached fees / gas: let viem prepare the tx, but the burner client has no bound
          // account so it must be supplied here (unlike the local-sign path above).
          txHash = await this.walletClient!.writeContract({
            ...tx,
            account: this.account!,
            nonce: this.nonce,
          } as any);
        }
        this.nonce += 1;

        this.recordContractTx(txHash, tx, false, turnId);

        return txHash;
      } catch (error: any) {
        await this.resyncNonceAndFees();
        this.handleTxError(error);
        throw error;
      }
    });
  }

  // Hand-built EIP-1559 sign for the burner: chainId/fees/nonce are all local, so the only
  // network round trip left is the send itself. Requires explicit gas (no estimation here).
  private signBurnerTx<TAbi extends Abi>(tx: WriteContractParameters<TAbi>, gas: bigint): Promise<Hex> {
    return this.account!.signTransaction({
      chainId: this.publicClient.chain!.id,
      type: 'eip1559',
      to: tx.address,
      data: encodeFunctionData({
        abi: tx.abi,
        functionName: tx.functionName,
        args: tx.args || [],
      } as any),
      nonce: this.nonce,
      gas,
      value: tx.value ?? 0n,
      maxFeePerGas: this.cachedMaxFeePerGas!,
      maxPriorityFeePerGas: 0n,
    });
  }

  // Runs `send` after every previously queued one, whatever their outcome. The chain
  // swallows results so a rejection can't wedge it or escape unhandled; callers still see it.
  private queueBurnerSend<T>(send: () => Promise<T>): Promise<T> {
    const result = this.burnerSends.then(send, send);
    this.burnerSends = result.then(() => {}, () => {});
    return result;
  }

  // Best-effort recovery after a failed burner send: the local nonce may be stale in either
  // direction (tx included despite the error, or never consumed) and cached fees may have lapsed.
  private async resyncNonceAndFees(): Promise<void> {
    if (!this.account) return;
    try {
      const [nonce, block] = await Promise.all([
        this.publicClient.getTransactionCount({ address: this.account.address, blockTag: 'pending' }),
        this.publicClient.getBlock({ blockTag: 'latest' }),
      ]);
      this.nonce = nonce;
      this.cachedMaxFeePerGas = block.baseFeePerGas != null ? block.baseFeePerGas * 2n : null;
    } catch { /* keep local state; the next failure retries the resync */ }
  }

  async sendRealtimeRawTransaction<TAbi extends Abi>(
    tx: WriteContractParameters<TAbi>,
    turnId?: number
  ): Promise<TransactionReceipt> {
    if (this.useExternalWalletForTransactions()) {
      try {
        const txHash = await this.writeViaExternalWallet(tx);

        const receipt = await this.publicClient.waitForTransactionReceipt({
          hash: txHash,
        });

        this.recordContractTx(receipt.transactionHash, tx, true, turnId);

        return receipt;
      } catch (error: any) {
        this.handleTxError(error);
        throw error;
      }
    }

    if (!this.walletClient || !this.account) {
      throw new Error('Wallet not connected');
    }

    // Don't sign until this account's nonce is loaded (no-op once sign-in has completed).
    await this.nonceReady;

    return this.queueBurnerSend(async () => {
      try {
        if (!tx.functionName || !tx.abi) {
          throw new Error('Either functionName/abi or data must be provided');
        }

        let signature: Hex;
        if (tx.gas && this.cachedMaxFeePerGas != null) {
          // Explicit gas + cached fees: sign locally — the realtime send is the only RPC.
          signature = await this.signBurnerTx(tx, tx.gas);
        } else {
          // Estimation path: prepare fills gas; cached fees (when known) skip the fee-fill read.
          const request = await this.publicClient.prepareTransactionRequest({
            account: this.account!,
            chain: this.walletClient!.chain,
            to: tx.address,
            data: encodeFunctionData({
              abi: tx.abi,
              functionName: tx.functionName,
              args: tx.args || [],
            } as any),
            nonce: this.nonce,
            value: tx.value || 0n,
            gas: tx.gas,
            ...(this.cachedMaxFeePerGas != null
              ? { maxFeePerGas: this.cachedMaxFeePerGas, maxPriorityFeePerGas: 0n }
              : {}),
          });
          signature = await this.walletClient!.signTransaction({
            ...request,
            account: this.account!,
            chain: this.walletClient!.chain,
          });
        }

        const receipt = await this.walletClient!.realtimeSendRawTransaction({
          serializedTransaction: signature,
        });
        this.nonce += 1;

        this.recordContractTx(receipt.transactionHash, tx, true, turnId);

        return receipt;
      } catch (error: any) {
        await this.resyncNonceAndFees();
        this.handleTxError(error);
        throw error;
      }
    });
  }

  // Testnet auto-drip waits for a chosen username so a drive-by page load doesn't
  // burn a drip. Retried on sign-in and on naming; fires at most once per session.
  private maybeAutoFaucet() {
    if (this.autoFaucetFired || !this.account) return;
    if (environment.network !== 'testnet' || !isDeployedSite()) return;
    if (!localStorage.getItem(this.USERNAME_KEY)) return;
    if (this.gameAccountBalance() !== 0n) return;

    this.autoFaucetFired = true;
    this.askFaucet();
  }

  async askFaucet() {
    const response = await fetch(
      environment.faucetEndpoint + '/' + this.account?.address
    );

    const body = await response.json();
    this.emitFaucetTx(body.hash, 'faucet');

    await this.awaitDripAndSyncBalance(body.hash);
  }

  // Balance is read at the drip's own block, so the refresh can't observe a pre-drip state.
  private async awaitDripAndSyncBalance(hash: Hex): Promise<void> {
    const txReceipt = await this.publicClient.waitForTransactionReceipt({ hash });
    const ethBalance = await this.publicClient.getBalance({
      address: this.account!.address,
      blockNumber: txReceipt.blockNumber,
    });
    this.gameAccountBalance.set(ethBalance);
  }

  // The drip is only handed out against the wallet's own refund, which the server spells
  // out in full: it can only ever move the starter gas back, and since the server pins it
  // to nonce 0, the wallet's first real tx kills it. Burner-only — a key the player
  // brought with them never signs one.
  async askFrensFaucet(frens: FrensTicketPayload) {
    if (!this.account || this.keyStorage.hasImportedKey()) return;

    const { ret } = frens;
    const returnRaw = await this.account.signTransaction({
      type: 'eip1559',
      chainId: ret.chainId,
      to: ret.to,
      nonce: ret.nonce,
      gas: BigInt(ret.gas),
      value: BigInt(ret.value),
      maxFeePerGas: BigInt(ret.maxFeePerGas),
      maxPriorityFeePerGas: BigInt(ret.maxPriorityFeePerGas),
    });

    const response = await fetch(
      environment.frensFaucetEndpoint + '/' + this.account.address,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket: frens.ticket, returnRaw }),
      }
    );
    if (!response.ok) return;

    const body = await response.json();
    if (!body?.hash) return;

    this.emitFaucetTx(body.hash, 'frens-faucet');

    await this.awaitDripAndSyncBalance(body.hash);
  }

  async signMessage(message: string): Promise<Hex | null> {
    if (this.useExternalWalletForTransactions() && this.externalWalletClient) {
      return this.externalWalletClient.signMessage({
        account: this.externalWalletAddress()!,
        message,
      });
    }
    if (this.walletClient && this.account) {
      return this.walletClient.signMessage({
        account: this.account,
        message,
      });
    }
    return null;
  }

  async sendAddressAuth(): Promise<void> {
    if (this.savedProviderRdns && !this.useExternalWalletForTransactions()) {
      return;
    }

    const activeAddr = this.activeAddress();
    if (!activeAddr) return;
    if (activeAddr === this.lastAuthedAddress) return;

    if (this.pendingAuth && this.pendingAuth.address === activeAddr) {
      return this.pendingAuth.promise;
    }

    const promise = this.runAddressAuth(activeAddr);
    this.pendingAuth = { address: activeAddr, promise };
    try {
      await promise;
    } finally {
      if (this.pendingAuth?.promise === promise) this.pendingAuth = null;
    }
  }

  private async runAddressAuth(activeAddr: Lowercase<Hex>): Promise<void> {
    const storedValue = localStorage.getItem(this.USERNAME_KEY);
    this.username.set(storedValue ? storedValue : activeAddr.slice(0, 6));

    const timestamp = Math.floor(Date.now() / 1000 / this.TIME_DENOM);

    let signature: Hex | null;
    const cached = this.cachedAuth;
    if (cached && cached.address === activeAddr && cached.timestamp === timestamp) {
      signature = cached.signature;
    } else {
      signature = await this.signMessage(timestamp.toString());
      if (!signature) return;
      this.cachedAuth = { address: activeAddr, timestamp, signature };
    }

    // The user may have disconnected the external wallet mid-sign; bail.
    if (this.activeAddress() !== activeAddr) return;

    const subscriptionMessage: Request = {
      type: RequestType.ADDRESS_AUTH,
      payload: {
        address: activeAddr,
        signature: signature,
        username: this.username(),
      },
    };
    this.server.queueMessage(subscriptionMessage);
    this.lastAuthedAddress = activeAddr;

    this.usernameSet.next(true);
  }

  async setUsername(username: string) {
    const activeAddr = this.activeAddress();
    if (!activeAddr) return;

    // Mirror belch's sanitizeUsername: strip leading @/whitespace so a
    // self-chosen name can't impersonate a verified X handle rendered
    // as `@handle 𝕏`. Keeps the UI in lockstep with what the server
    // will store.
    const cleaned = username.replace(/^[@\s]+/, '').slice(0, 12).trim();

    localStorage.setItem(this.USERNAME_KEY, cleaned);
    this.username.set(cleaned);
    this.maybeAutoFaucet();

    if (this.lastAuthedAddress === activeAddr) {
      // Connect already auto-authed with an `addr.slice(0,6)` placeholder, so a
      // re-auth would no-op server-side — push the real name via SET_USERNAME.
      const setUsernameMessage: Request = {
        type: RequestType.SET_USERNAME,
        payload: {
          username: cleaned,
        },
      };
      this.server.queueMessage(setUsernameMessage);
    } else {
      // Not yet authed — auth now; runAddressAuth reads the stored username.
      await this.sendAddressAuth();
    }
  }

  async connectToProvider(providerDetail: EIP6963ProviderDetail): Promise<void> {
    try {
      this.externalWalletClient = createWalletClient({
        chain: this.publicClient.chain,
        transport: custom(providerDetail.provider as any),
      });

      const addresses = await this.externalWalletClient.requestAddresses();

      if (!addresses || addresses.length === 0) {
        throw new Error('No accounts found in browser wallet');
      }

      const address = addresses[0].toLowerCase() as Lowercase<Hex>;
      this.externalWalletAddress.set(address);
      this.connectedProviderRdns.set(providerDetail.info.rdns);

      await this.switchOrAddChain(providerDetail.provider);
    } catch (error) {
      this.externalWalletClient = undefined;
      this.externalWalletAddress.set(undefined);
      this.connectedProviderRdns.set(undefined);
      throw error;
    }
  }

  // Returns the list of available wallets if multiple are found.
  async connectBrowserWallet(
    providerDetail?: EIP6963ProviderDetail
  ): Promise<EIP6963ProviderDetail[] | void> {
    if (providerDetail) {
      await this.connectToProvider(providerDetail);
      return;
    }

    const availableWallets = this.getAvailableWallets();

    if (availableWallets.length > 1) {
      return availableWallets;
    }

    if (availableWallets.length === 1) {
      await this.connectToProvider(availableWallets[0]);
      return;
    }

    if (typeof window === 'undefined' || !(window as any).ethereum) {
      throw new Error(
        'No browser wallet detected. Please install MetaMask or another Web3 wallet.'
      );
    }

    // Legacy providers carry no EIP-6963 metadata; the empty rdns keeps them out of the
    // auto-reconnect lookup, which is what the previous inline path did by omission.
    await this.connectToProvider({
      info: { uuid: '', name: 'Browser Wallet', icon: '', rdns: '' },
      provider: (window as any).ethereum as EIP1193Provider,
    });
  }

  // Order matters: flip the preference first so sendAddressAuth signs with the
  // external wallet, then load state and mirror the balance back.
  async adoptExternalWallet(): Promise<void> {
    this.useExternalWalletForTransactions.set(true);
    this.persistExternalWalletChoice();
    await this.sendAddressAuth();
    await this.onchainState.loadStateForAddress(this.externalWalletAddress()!);
    this.externalWalletBalance.set(this.onchainState.ethBalance());
  }

  // Local-only disconnect: drops the EIP-6963 client and clears displayed state.
  // The wallet provider itself isn't notified — there's no standard EIP method
  // to revoke a connection, so we just stop using it on our side.
  async disconnectExternalWallet(): Promise<void> {
    this.externalWalletClient = undefined;
    this.externalWalletAddress.set(undefined);
    this.externalWalletBalance.set(0n);
    this.useExternalWalletForTransactions.set(false);
    this.connectedProviderRdns.set(undefined);
    this.clearExternalWalletChoice();
    // Reload chain state for the burner; OnchainStateService held the external
    // wallet's view (e.g. empty owned-mons), and consumers like hasTeam keyed
    // off that would otherwise stay stale.
    const burner = this.walletAddress();
    if (burner) {
      await this.onchainState.loadStateForAddress(burner);
      this.gameAccountBalance.set(this.onchainState.ethBalance());
    }
  }

  private readSavedProviderRdns(): string | null {
    try {
      if (localStorage.getItem(Wallet.EXTERNAL_PREF_KEY) !== 'true') return null;
      return localStorage.getItem(Wallet.EXTERNAL_PROVIDER_RDNS_KEY);
    } catch {
      return null;
    }
  }

  private persistExternalWalletChoice(): void {
    const rdns = this.connectedProviderRdns();
    try {
      localStorage.setItem(Wallet.EXTERNAL_PREF_KEY, 'true');
      if (rdns) {
        localStorage.setItem(Wallet.EXTERNAL_PROVIDER_RDNS_KEY, rdns);
      }
    } catch {}
    this.savedProviderRdns = rdns ?? null;
  }

  private clearExternalWalletChoice(): void {
    try {
      localStorage.removeItem(Wallet.EXTERNAL_PREF_KEY);
      localStorage.removeItem(Wallet.EXTERNAL_PROVIDER_RDNS_KEY);
    } catch {}
    this.savedProviderRdns = null;
    // Gate at sendAddressAuth() lifts now; no forceReconnect on this ws will
    // re-trigger it, so kick the auth manually.
    void this.sendAddressAuth();
  }

  // Triggered once when the saved EIP-6963 provider announces; signMessage
  // still prompts (cache is in-memory) — that's the intended sign-in UX.
  private async tryAutoReconnectExternal(detail: EIP6963ProviderDetail): Promise<void> {
    if (this.autoReconnectAttempted) return;
    if (!this.savedProviderRdns || this.savedProviderRdns !== detail.info.rdns) return;
    this.autoReconnectAttempted = true;
    try {
      await this.connectToProvider(detail);
      await this.adoptExternalWallet();
    } catch (error) {
      console.warn('Auto-reconnect to external wallet failed:', error);
      this.clearExternalWalletChoice();
    }
  }

  // Public chain-switch utility for the independent market wallet (own connection state,
  // shared chain plumbing).
  // Defaults to MegaETH; the funding flow passes a source chain instead, so the
  // switch/add pair lives here once rather than per external-wallet service.
  ensureProviderChain(provider: EIP1193Provider, chain?: ProviderChain): Promise<void> {
    return this.switchOrAddChain(provider, chain);
  }

  private async switchOrAddChain(
    provider: EIP1193Provider,
    chain: ProviderChain = {
      chainId: environment.chainId,
      chainName: environment.chainName,
      rpcUrl: environment.publicRPCEndpoint,
      explorerUrl: EXPLORER_LINK,
    }
  ): Promise<void> {
    const hexChainId = `0x${chain.chainId.toString(16)}`;
    try {
      await provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: hexChainId }],
      });
    } catch (switchError: any) {
      // This error code indicates that the chain has not been added
      if (switchError.code === 4902) {
        await provider.request({
          method: 'wallet_addEthereumChain',
          params: [
            {
              chainId: hexChainId,
              chainName: chain.chainName,
              nativeCurrency: {
                name: `${chain.chainName} Ether`,
                symbol: 'ETH',
                decimals: 18,
              },
              rpcUrls: [chain.rpcUrl],
              ...(chain.explorerUrl ? { blockExplorerUrls: [chain.explorerUrl] } : {}),
            },
          ],
        });
      } else {
        throw switchError;
      }
    }
  }

  // Caller is responsible for the rethrow; this only routes the error to the
  // right surface (funding modal vs. generic web3 toast). Without a relay endpoint
  // the modal has nothing to offer, so the error goes to the toast instead.
  private handleTxError(error: unknown): void {
    if (this.isInsufficientFundsError(error) && !!environment.relayEndpoint) {
      this.showFundingModal.set(true);
    } else {
      this.web3Error.pushError(error);
    }
  }

  // Viem only auto-wraps standard JSON-RPC methods as InsufficientFundsError; the MegaETH realtime
  // path and provider errors come back generic, so fall back to a string match across the error chain.
  private isInsufficientFundsError(error: unknown): boolean {
    if (error instanceof InsufficientFundsError) return true;

    if (error instanceof BaseError && error.walk((e) => e instanceof InsufficientFundsError)) {
      return true;
    }

    return hasInsufficientFundsText(error);
  }

  formatBalance(balance: bigint): string {
    return formatEth(balance);
  }

  // Exact wei→ETH string (no rounding, trailing zeros trimmed) for amount inputs.
  formatBalanceExact(balance: bigint): string {
    return formatEther(balance);
  }

  // Battle-hot clients talk to MegaETH directly, fail over to the proxy in one attempt.
  // OnchainStateService stays on the proxy: bulk cached hydration, not latency-sensitive.
  // Only reads batch: a batched realtime send would hold its neighbours' responses until it lands.
  private directFirstTransport(batch = false) {
    return fallback(
      [
        http(environment.publicRPCEndpoint, { batch, fetchOptions: { cache: 'no-store' }, retryCount: 0 }),
        http(this.rpc, { batch, fetchOptions: { cache: 'no-store' } }),
      ],
      { rank: false }
    );
  }

  get burnerTx() {
    return { account: this.account!, chain: this.publicClient.chain! };
  }

  private async balanceOf(address: Lowercase<Hex> | undefined): Promise<bigint> {
    if (!address) {
      return 0n;
    }
    return this.publicClient.getBalance({ address });
  }

  async getGameAccountBalance(): Promise<bigint> {
    return this.balanceOf(this.walletAddress());
  }

  async getExternalWalletBalance(): Promise<bigint> {
    return this.balanceOf(this.externalWalletAddress());
  }

  async refreshGameAccountBalance(): Promise<void> {
    const balance = await this.getGameAccountBalance();
    this.gameAccountBalance.set(balance);
  }

  async refreshExternalWalletBalance(): Promise<void> {
    const balance = await this.getExternalWalletBalance();
    this.externalWalletBalance.set(balance);
  }

  async refreshBalances(): Promise<void> {
    await Promise.all([
      this.refreshGameAccountBalance(),
      this.refreshExternalWalletBalance(),
    ]);
  }

  // Deposit ETH from external wallet to game account; amount in ETH (e.g. '0.001').
  async depositToGameAccount(amount: string): Promise<Hex> {
    if (!this.externalWalletClient || !this.externalWalletAddress()) {
      throw new Error('Please connect your browser wallet first');
    }

    if (!this.walletAddress()) {
      throw new Error('Game wallet not initialized');
    }

    const hash = await this.externalWalletClient.sendTransaction({
      account: this.externalWalletAddress()!,
      to: this.walletAddress()!,
      value: parseEther(amount),
      chain: this.externalWalletClient.chain,
      gas: Wallet.TRANSFER_GAS,
    });

    await this.publicClient.waitForTransactionReceipt({
      hash: hash,
    });

    await this.refreshBalances();

    return hash;
  }

  // Send ETH from game account to any address; amount in ETH (e.g. '0.001').
  async sendToAddress(toAddress: Hex, amount: string): Promise<Hex> {
    if (!this.walletClient || !this.account) {
      throw new Error('Game wallet not initialized');
    }

    const hash = await this.walletClient.sendTransaction({
      account: this.account,
      to: toAddress,
      value: parseEther(amount),
      chain: this.walletClient.chain,
      gas: Wallet.TRANSFER_GAS,
    });

    await this.publicClient.waitForTransactionReceipt({
      hash: hash,
    });

    await this.refreshGameAccountBalance();

    return hash;
  }

  async signTypedData(params: {
    domain: {
      name: string;
      version: string;
      chainId: number;
      verifyingContract: Hex;
    };
    types: Record<string, readonly { name: string; type: string }[]>;
    primaryType: string;
    message: Record<string, unknown>;
  }): Promise<Hex> {
    const { domain, types, primaryType, message } = params;

    if (this.useExternalWalletForTransactions() && this.externalWalletClient) {
      return await this.externalWalletClient.signTypedData({
        account: this.externalWalletAddress()!,
        domain,
        types,
        primaryType,
        message,
      } as any);
    } else if (this.walletClient && this.account) {
      return await this.walletClient.signTypedData({
        account: this.account,
        domain,
        types,
        primaryType,
        message,
      } as any);
    }

    throw new Error('No wallet available for signing');
  }

  private getSignedCommitManagerDomain() {
    return {
      ...SIGNED_COMMIT_MANAGER_DOMAIN,
      chainId: this.publicClient.chain!.id,
      verifyingContract: Address.COMMIT_MANAGER,
    };
  }

  // EIP-712 domain for the Engine's built-in dual-signed buffer flow —
  // verifyingContract is the Engine itself, not the deprecated SignedCommitManager.
  private getEngineDomain() {
    return {
      ...CHOMP_ENGINE_DOMAIN,
      chainId: this.publicClient.chain!.id,
      verifyingContract: Address.ENGINE,
    };
  }

  // Sign the move hash that commits the committing player to their move.
  async signCommit(commit: SignedCommit): Promise<Hex> {
    return this.signTypedData({
      domain: this.getSignedCommitManagerDomain(),
      types: SignedCommitTypes,
      primaryType: 'SignedCommit',
      message: {
        moveHash: commit.moveHash,
        battleKey: commit.battleKey,
        turnId: commit.turnId,
      },
    });
  }

  // Revealing player signs over the committer's hash plus the revealer's full move data.
  async signDualReveal(reveal: DualSignedReveal): Promise<Hex> {
    return this.signTypedData({
      domain: this.getEngineDomain(),
      types: DualSignedRevealTypes,
      primaryType: 'DualSignedReveal',
      message: {
        battleKey: reveal.battleKey,
        turnId: reveal.turnId,
        committerMoveHash: reveal.committerMoveHash,
        revealerMoveIndex: reveal.revealerMoveIndex,
        revealerSalt: reveal.revealerSalt,
        revealerExtraData: reveal.revealerExtraData,
      },
    });
  }

  // 2-slot flow: the revealing side signs over the committer's whole-side hash plus its own full
  // side word. Engine domain, same as signDualReveal.
  async signSlotDualReveal(reveal: DualSignedSlotReveal): Promise<Hex> {
    return this.signTypedData({
      domain: this.getEngineDomain(),
      types: DualSignedSlotRevealTypes,
      primaryType: 'DualSignedSlotReveal',
      message: {
        battleKey: reveal.battleKey,
        turnId: reveal.turnId,
        committerMovesHash: reveal.committerMovesHash,
        revealerSidePacked: reveal.revealerSidePacked,
      },
    });
  }
}

// Walk the error chain (cause / details / shortMessage / message) and look for
// the canonical "insufficient funds" / "insufficient balance" RPC text. Covers
// provider errors and custom-RPC errors that viem doesn't auto-classify.
function hasInsufficientFundsText(error: unknown, depth = 0): boolean {
  if (error == null || depth > 5) return false;
  if (typeof error === 'string') return /insufficient (funds|balance)/i.test(error);
  if (typeof error !== 'object') return false;

  const e = error as Record<string, unknown>;
  for (const key of ['message', 'shortMessage', 'details', 'reason']) {
    const v = e[key];
    if (typeof v === 'string' && /insufficient (funds|balance)/i.test(v)) return true;
  }
  if (hasInsufficientFundsText(e['cause'], depth + 1)) return true;
  return false;
}
